import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Send, MessageSquarePlus, MoreVertical, Ban, Flag } from "lucide-react";

function timeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString();
}

export default function Messages() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // { other_user_id, other_name }
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [directory, setDirectory] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [newTo, setNewTo] = useState("");
  const [newText, setNewText] = useState("");
  const endRef = useRef(null);
  const [openReport, setOpenReport] = useState(false);
  const [reportReason, setReportReason] = useState("");

  const loadConvs = async () => {
    const { data } = await api.get("/messages/conversations");
    setConvs(data);
  };

  const loadThread = async (otherId) => {
    const { data } = await api.get(`/messages/with/${otherId}`);
    setThread(data);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  useEffect(() => {
    loadConvs();
    api.get("/messages/directory").then((r) => {
      setDirectory(r.data.filter((u) => u.user_id !== user.user_id));
    });
    const t = setInterval(() => {
      loadConvs();
      if (active) loadThread(active.other_user_id);
    }, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [active?.other_user_id]);

  const openConv = async (c) => {
    setActive({ other_user_id: c.other_user_id, other_name: c.other_name });
    await loadThread(c.other_user_id);
    loadConvs();
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", { to_user_id: active.other_user_id, text });
      setText("");
      await loadThread(active.other_user_id);
      loadConvs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send.");
    }
  };

  const startNew = async () => {
    if (!newTo || !newText.trim()) return;
    try {
      await api.post("/messages", { to_user_id: newTo, text: newText });
      const other = directory.find((d) => d.user_id === newTo);
      setActive({ other_user_id: newTo, other_name: other?.name || "User" });
      setOpenNew(false);
      setNewTo(""); setNewText("");
      await loadConvs();
      await loadThread(newTo);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message.");
    }
  };

  const blockUser = async () => {
    if (!active) return;
    try {
      await api.post("/users/block", { user_id: active.other_user_id });
      toast.success(`${active.other_name} blocked. They can no longer message you.`);
      setActive(null);
      loadConvs();
    } catch {
      toast.error("Could not block user.");
    }
  };

  const reportUser = async () => {
    if (!active || !reportReason.trim()) return;
    try {
      await api.post("/users/report", { user_id: active.other_user_id, reason: reportReason });
      toast.success("Report submitted to admins.");
      setOpenReport(false);
      setReportReason("");
    } catch {
      toast.error("Could not submit report.");
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-md overflow-hidden grid grid-cols-1 md:grid-cols-3 min-h-[500px]">
      {/* Conversation list */}
      <div className="border-r border-stone-200 bg-stone-50/50">
        <div className="p-4 border-b border-stone-200 flex items-center justify-between">
          <div className="font-serif text-lg text-stone-900">Messages</div>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="new-message-btn" className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
                <MessageSquarePlus size={14} className="mr-1" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-stone-50">
              <DialogHeader><DialogTitle className="font-serif text-2xl">New Message</DialogTitle></DialogHeader>
              <Select value={newTo} onValueChange={setNewTo}>
                <SelectTrigger data-testid="new-message-to" className="bg-white"><SelectValue placeholder="Select recipient (farmer or admin)" /></SelectTrigger>
                <SelectContent>
                  {directory.map((d) => (
                    <SelectItem key={d.user_id} value={d.user_id}>{d.name} · {d.role}{d.state ? ` · ${d.state}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input data-testid="new-message-text" placeholder="Write a message..." value={newText} onChange={(e) => setNewText(e.target.value)} className="bg-white" />
              <DialogFooter><Button data-testid="new-message-send" onClick={startNew} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Send</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {convs.length === 0 ? (
            <div className="p-6 text-sm text-stone-500 font-sans">No conversations yet.</div>
          ) : convs.map((c) => (
            <button
              key={c.conversation_id}
              data-testid={`conv-${c.other_user_id}`}
              onClick={() => openConv(c)}
              className={`w-full text-left p-4 border-b border-stone-100 hover:bg-stone-100 transition-colors ${active?.other_user_id === c.other_user_id ? "bg-stone-100" : ""}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="font-serif text-base text-stone-900 truncate">{c.other_name}</div>
                {c.unread > 0 && <span className="bg-emerald-800 text-stone-50 text-[10px] px-2 py-0.5 rounded-full font-sans font-semibold">{c.unread}</span>}
              </div>
              <div className="text-xs text-stone-500 mt-1 font-sans truncate">{c.last_message}</div>
              <div className="text-[10px] text-stone-400 mt-1 font-sans">{timeShort(c.last_at)}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Thread */}
      <div className="md:col-span-2 flex flex-col">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-stone-500 font-sans p-10 text-center">
            Select a conversation or start a new one.
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-stone-200 flex items-center justify-between">
              <div className="font-serif text-lg text-stone-900">{active.other_name}</div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="thread-menu" className="w-8 h-8 rounded-md hover:bg-stone-100 flex items-center justify-center text-stone-500">
                    <MoreVertical size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuItem data-testid="block-user-btn" onClick={blockUser} className="text-stone-700 cursor-pointer">
                    <Ban size={14} className="mr-2" /> Block user
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="report-user-btn" onClick={() => setOpenReport(true)} className="text-stone-700 cursor-pointer">
                    <Flag size={14} className="mr-2" /> Report to admin
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[500px] bg-stone-50/30">
              {thread.map((m) => {
                const mine = m.from_user_id === user.user_id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2 rounded-md font-sans text-sm ${mine ? "bg-emerald-800 text-stone-50" : "bg-white border border-stone-200 text-stone-800"}`}>
                      <div>{m.text}</div>
                      <div className={`text-[10px] mt-1 ${mine ? "text-stone-200" : "text-stone-400"}`}>{timeShort(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="p-4 border-t border-stone-200 flex gap-2 bg-white">
              <Input
                data-testid="message-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type a message..."
                className="bg-stone-50"
              />
              <Button data-testid="message-send" onClick={send} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
                <Send size={16} />
              </Button>
            </div>
          </>
        )}
      </div>

      <Dialog open={openReport} onOpenChange={setOpenReport}>
        <DialogContent className="bg-stone-50">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Report {active?.other_name}</DialogTitle></DialogHeader>
          <Textarea
            data-testid="report-reason"
            placeholder="What happened? Admins will review this report."
            rows={4}
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            className="bg-white"
          />
          <DialogFooter>
            <Button data-testid="report-submit" onClick={reportUser} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Submit Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
